// Em dash shown when a figure is unavailable (e.g. closed portfolios), per design.
export const EM_DASH = '—';

// Client-portal's empty-currency marker (its emptyCurrency('USD')), shown for
// unavailable currency figures.
export const EMPTY_CURRENCY = '$-';

// Finite number from a raw report figure; null for absent, empty, or non-numeric input.
export const toNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : null;
};

/**
 * Compact currency, unsigned. Positives have no leading sign; negatives use the
 * accounting-style parentheses of formatFullCurrency and the shared formatCurrency.
 * Returns an em dash for null/undefined/NaN input.
 *   2_402_118  -> "$2.40M"
 *     350_154  -> "$350k"
 *        842   -> "$842"
 *  -1_893_555  -> "( $1.89M )"
 */
export const formatCompactCurrency = (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) {
        return EM_DASH;
    }
    const abs = Math.abs(n);
    let body: string;
    if (abs >= 1e6) {
        body = `$${(abs / 1e6).toFixed(2)}M`;
    } else if (abs >= 1e3) {
        body = `$${Math.round(abs / 1e3)}k`;
    } else {
        body = `$${abs.toFixed(0)}`;
    }
    // As in formatFullCurrency: no parentheses for a negative that rounds to zero ("$0", not "( $0 )").
    return n < 0 && body !== '$0' ? `( ${body} )` : body;
};

/**
 * Full currency value with thousands separators, two decimal places and no
 * magnitude suffix — matching the client-portal `formatCurrency(value, 'USD', 2)`
 * convention used in its detailed performance report. Negatives use
 * accounting-style parentheses; nullish or non-numeric input returns the
 * client-portal empty-currency marker "$-".
 *   2_402_118  -> "$2,402,118.00"
 *     350_154  -> "$350,154.00"
 *        842   -> "$842.00"
 *  -1_893_555  -> "( $1,893,555.00 )"
 *       null   -> "$-"
 */
export const formatFullCurrency = (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) {
        return EMPTY_CURRENCY;
    }
    const body = `$${Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
    // Match client-portal: only apply the negative parentheses when the value is
    // non-zero at the displayed precision, so -0.001 renders as "$0.00" not "( $0.00 )".
    const isNegative = n < 0 && Math.abs(Number(n.toFixed(2))) > 0;
    return isNegative ? `( ${body} )` : body;
};

/**
 * Compact currency with an explicit sign on positives (+$2.40M / ( $2k )). Used for
 * headline gain figures (e.g. Net Earnings). Returns an em dash for nullish input.
 */
export const formatSignedCompactCurrency = (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) {
        return EM_DASH;
    }
    return n >= 0 ? `+${formatCompactCurrency(n)}` : formatCompactCurrency(n);
};

/**
 * Percent with fixed two decimals and thousands separators, matching the design's
 * holdings columns (the shared formatPercent drops trailing zeros).
 *   15.17  -> "15.17%"
 *   12.5   -> "12.50%"
 *   0      -> "0.00%"
 *   null   -> "—"
 */
export const formatFullPercent = (value: number | string | null | undefined): string => {
    const n = toNumber(value);
    if (n === null) {
        return EM_DASH;
    }
    return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

type TrendDirection = 'up' | 'down' | 'none';

/** Direction of a signed figure; exactly zero is neutral ('none'). Null for nullish input.
 *  Internal — consumed only by trendArrow, negativeColorClass and signColorClass below. */
const trendDirection = (value: number | string | null | undefined): TrendDirection | null => {
    const n = toNumber(value);
    if (n === null) {
        return null;
    }
    return n > 0 ? 'up' : n < 0 ? 'down' : 'none';
};

/** Up/down arrow glyph for a signed figure (▲ / ▼); empty string when flat ('none') or nullish. */
export const trendArrow = (value: number | string | null | undefined): string => {
    switch (trendDirection(value)) {
        case 'up':
            return '▲';
        case 'down':
            return '▼';
        case 'none': // exactly zero — flat, no glyph
        case null: // unavailable
            return '';
    }
};

/**
 * text-negative for a negative money figure that is not a gain/loss (an allocation amount, say), so
 * negatives read red everywhere while positives keep their neutral color. Undefined otherwise.
 */
export const negativeColorClass = (value: number | string | null | undefined): 'text-negative' | undefined =>
    trendDirection(value) === 'down' ? 'text-negative' : undefined;

/**
 * Tailwind text-color class for a gain/loss figure, using the semantic tokens.
 * Returns undefined when neutral or nullish so callers can fall back to a neutral color.
 */
export const signColorClass = (
    value: number | string | null | undefined,
): 'text-positive' | 'text-negative' | undefined => {
    const dir = trendDirection(value);
    if (dir === 'up') {
        return 'text-positive';
    }
    if (dir === 'down') {
        return 'text-negative';
    }
    return undefined;
};
